import { internalLogger } from '../../../interface/v1/clientOsLogger';
import bindAllMethods from '../../../utils/bindAllMethods';

import {
  ConsentServiceInputType,
  SetServiceDependencies
} from '../../../infra/startup/types';

import { setCookie } from '../../../utils/cookies';
import { SmartSharedServicesClient } from '../clients/SmartSharedServicesClient';

import { ConsentUpdateEventData } from './IConsentStrategy';
import { BaseConsentStrategy } from './BaseConsentStrategy';
import { ConsentResultType } from './types';
import { ValueStoreConsentMapper } from '../utils/ValueStoreConsentMapper';
import { SmartSharedServicesConsentMapper } from '../utils/SmartSharedServicesConsentMapper';
import {
  optanonAlertBoxClosedCookieName,
  optanonConsentCookieName
} from '../../../config/constants';

/**
 * Native consent strategy implementation
 * Handles consent management for native environments using ValueStore
 * with Smart-Shared-Services API fallback
 */
export class NativeConsentStrategy extends BaseConsentStrategy {
  // Configuration and state
  private smartSharedServicesClient: SmartSharedServicesClient;
  private _isNative: boolean;
  private readonly _strategyName = 'NativeConsentStrategy';

  constructor(params: ConsentServiceInputType) {
    super();
    this._isNative = params.isNative;

    bindAllMethods(this);
  }

  public setDependencies({ services, clients }: SetServiceDependencies): void {
    this._eventService = services.eventService;
    this.smartSharedServicesClient = clients.consents.userConsentClient;

    bindAllMethods(this);
  }

  public isSupported(): boolean {
    return this._isNative;
  }

  public getStrategyName(): string {
    return 'NativeConsentStrategy';
  }

  public async init(): Promise<void> {
    if (!this.isSupported()) {
      internalLogger?.warn(
        'NativeConsentStrategy: Not supported in current environment'
      );
      return;
    }

    try {
      // Execute native consent initialization flow
      await this._initializeNativeConsents();

      internalLogger?.debug?.(
        'NativeConsentStrategy: Initialization finished.'
      );
    } catch (error) {
      internalLogger?.error(
        'NativeConsentStrategy: Initialization failed:',
        error
      );
      throw error;
    }
  }

  public async refresh(): Promise<void> {
    if (!this.isSupported()) return;

    // Re-initialize consents on refresh
    await this._initializeNativeConsents();
  }

  /**
   * Update opt-in groups in ValueStore - native implementation
   * This is the main method that triggers consent synchronization
   */
  public async updateOptInGroupsInValueStore(): Promise<void> {
    try {
      await this._initializeNativeConsents();
    } catch (error) {
      internalLogger?.error(
        'NativeConsentStrategy: Failed to update opt-in groups:',
        error
      );
    }
  }

  /**
   * Core method that implements the native consent initialization flow
   * 1. ValueStore Lookup
   * 2. Fallback Mechanism
   * 3. Cookie Management
   * 4. Event Broadcasting
   * @private
   */
  private async _initializeNativeConsents(): Promise<void> {
    internalLogger?.debug?.(
      'NativeConsentStrategy: Starting consent initialization'
    );

    // ValueStore Lookup
    let consentResult =
      await ValueStoreConsentMapper.lookupConsentFromValueStore();
    let consentSource: ConsentUpdateEventData['source'] = 'valuestore';

    // Fallback Mechanism - triggered when the ValueStore call failed outright
    // OR when it succeeded but didn't return an explicit decision for all
    // expected keys (missing/null keys must not be treated as opt-out).
    if (consentResult.hasError || consentResult.isIncomplete) {
      internalLogger?.warn(
        'NativeConsentStrategy: ValueStore lookup failed or incomplete, trying Smart-Shared-Services fallback'
      );

      const fallbackResult =
        await SmartSharedServicesConsentMapper.getFallbackConsentFromAPI(
          this.smartSharedServicesClient
        );
      if (fallbackResult) {
        // Convert API response to Optanon format
        consentResult =
          SmartSharedServicesConsentMapper.convertAPIResponseToOptanon(
            fallbackResult
          );
        consentSource = 'smart-shared-services';
      } else if (!consentResult.hasError) {
        // ValueStore data was incomplete and the Consents API fallback could
        // not confirm it either - do not persist a possibly-wrong consent
        // state (e.g. an inferred opt-out) and bail out below.
        consentResult = {
          ...consentResult,
          hasError: true,
          errorMessage:
            'ValueStore consent data incomplete and Smart-Shared-Services fallback failed'
        };
      }
    }

    if (consentResult.hasError) {
      internalLogger?.error(
        'NativeConsentStrategy: All consent sources failed, not creating consents.'
      );
      return;
    }

    // Seting the consent cookies
    this._setCookiesAsOptanonCookies(consentResult);

    // Event Broadcasting
    await this._broadcastConsentUpdateEvent(consentResult, consentSource);
  }

  /**
   * Step 2: Set cookies for later consumption
   * @private
   */
  private _setCookiesAsOptanonCookies(consentResult: ConsentResultType): void {
    try {
      const optanonConsent = consentResult.optanonConsent;

      // Set OptanonConsent cookie
      setCookie(
        optanonConsentCookieName,
        optanonConsent,
        31536000, // 1 year,
        false
      );

      // Set OptanonAlertBoxClosed cookie
      setCookie(
        optanonAlertBoxClosedCookieName,
        'true',
        31536000, // 1 year,
        false
      );

      internalLogger?.debug?.('NativeConsentStrategy: Cookies updated.', {
        optanonConsent
      });
    } catch (error) {
      internalLogger?.error(
        'NativeConsentStrategy: Failed to set cookies',
        error
      );
    }
  }

  /**
   * Step 4: Broadcast consent update event
   * @private
   */
  private async _broadcastConsentUpdateEvent(
    consentResult: ConsentResultType,
    source: ConsentUpdateEventData['source']
  ): Promise<void> {
    try {
      const eventData: ConsentUpdateEventData = {
        source,
        optanonConsent: consentResult.optanonConsent,
        timestamp: Date.now()
      };

      if (this.onConsentUpdated) {
        await this.onConsentUpdated(eventData);
      }
    } catch (error) {
      internalLogger?.error(
        'NativeConsentStrategy: Failed to broadcast consent update event:',
        error
      );
    }
  }
}
